import _ from 'lodash';
import ui from './ui';
import Document from './document';
import ComponentsBatchFunctions from './privates/utils/componentsBatchFunctions';
import {types} from './privates/utils';
import {onReady} from './privates/readyManager';
import wixSelectorUtils from './privates/wixSelectorUtils';
import HiddenCollapsedElement from './ui/HiddenCollapsedElement';
import monitoring from './privates/monitoring';
import WixBaseEvent from './ui/events/WixBaseEvent';
import {errors} from './privates/validations/errors';
import platformUtils from './privates/platformUtils';
import ghostMixin from './privates/utils/ghostMixin';

const isCompName = selector => selector.trim().indexOf('#') === 0;
const isMulti = selector => selector.indexOf(',') > -1;

class WixSelector {
    initialize({currentRMI, contextId, globalRMI}) {
        this.currentRMI = currentRMI;
        this.globalRMI = globalRMI || currentRMI;
        this.contextId = contextId;
    }

    rendered(controllerId, logName, selector, onlyNestedScope) {
     // let $w perform its basic validations
     const res = this.$w(controllerId, logName, selector, onlyNestedScope);

     if(!isCompName(selector) || isMulti(selector)) {
        throw new monitoring.MonitoringErrorToIgnore(errors.ERROR_INVALID_SELECTOR);
     }

     if(_.isArray(res) && res.length === 0) {
         return false;
     }

     return res.rendered;
    }

    scoped$W(controllerId, logName) {
        const $w = (selector) => this.$w(controllerId, logName, selector, false);
        const createEvent = (type, params) => this.createEvent(type, params);
        const at = (context) => this.at($w, controllerId, context);

        $w.createEvent = createEvent;
        $w.at = at;
        $w.rendered = (selector) => this.rendered(controllerId, logName, selector, false);

        $w.scoped = (selector) => this.$w(controllerId, logName, selector, true);

        $w.scoped.createEvent = createEvent;
        $w.scoped.at = at;
        return $w;
    }

    $w(controllerId, logName, compSelector, onlyNestedScope) {

        if (_.isFunction(compSelector)) {
            onReady(compSelector, logName);
            return;
        }

        if (!_.isString(compSelector)) {
            throw new monitoring.MonitoringErrorToIgnore(errors.ERROR_SELECTOR_TYPE_STRING);
        }

        if (!this.currentRMI) {
            throw new monitoring.MonitoringErrorToIgnore(errors.ERROR_SELECTOR_BEFORE_ON_READY);
        }

        const scopedRMI = this.currentRMI.getScopedRMI(controllerId);
        const wixComps = [];

        const isRole = (compSelector) => compSelector.trim().indexOf('@') === 0;
        const isType = (compSelector) => types.getPotentialViewerTypes(compSelector).length > 0 || compSelector === 'PageContent';
        const getType = (compSelector) => { return compSelector === 'PageContent' ? 'Page' : compSelector; };

        const isDocument = (compSelector) => compSelector === 'Document';
        const selector = compSelector.trim().substr(1);

        switch (true) {
            case isDocument(compSelector):
                const cIds = wixSelectorUtils.getCompIdsForRole(scopedRMI, selector, onlyNestedScope);
                return new Document(scopedRMI, cIds[0], this.contextId, this, controllerId, undefined, {compType: 'Document'});
            case isType(compSelector):
                const type = getType(compSelector);
                const comps = wixSelectorUtils.getCompsForType(types, scopedRMI, type, onlyNestedScope);
                comps.forEach((comp) => {
                    wixComps.push(this.getInstance(scopedRMI, comp.type, comp.id, controllerId));
                });
                return ComponentsBatchFunctions.expose(wixComps);
            case isMulti(compSelector):
                const selectors = compSelector.split(',');
                const selectedComps = [];
                selectors.forEach((comp) => {
                    if (isCompName(comp)) {
                        const sel = comp.trim().substr(1);
                        const id = wixSelectorUtils.getCompIdsForRole(scopedRMI, sel, onlyNestedScope)[0];
                        const component = this.getInstance(scopedRMI, scopedRMI.getType(id), id, controllerId);
                        if (!_.isArray(component)) {
                            const isCompInArr = _.find(selectedComps, {id});
                            if (!isCompInArr) {
                                selectedComps.push({
                                    id,
                                    type: scopedRMI.getType(id)
                                });
                            }
                        }
                    } else if (isType(comp.trim())) {
                        const type = getType(comp.trim());
                        const comps = wixSelectorUtils.getCompsForType(types, scopedRMI, type);
                        comps.forEach((comp) => {
                            const isCompInArr = _.find(selectedComps, {id: comp.id});
                            if (!isCompInArr) {
                                selectedComps.push(comp);
                            }
                        });
                    }
                });
                selectedComps.forEach((comp) => {
                    wixComps.push(this.getInstance(scopedRMI, comp.type, comp.id, controllerId));
                });
                return ComponentsBatchFunctions.expose(_.without(wixComps, undefined));
            case isCompName(compSelector):
                const ids = wixSelectorUtils.getCompIdsForRole(scopedRMI, selector, onlyNestedScope);
                return !_.isEmpty(ids) ? this.getInstance(scopedRMI, scopedRMI.getType(ids[0]), ids[0], controllerId) : [];
            case isRole(compSelector):
                const compIds = wixSelectorUtils.getCompIdsForRole(scopedRMI, selector, onlyNestedScope);
                compIds.forEach((compId) => {
                    wixComps.push(this.getInstance(scopedRMI, scopedRMI.getType(compId), compId, controllerId, selector));
                });
                return ComponentsBatchFunctions.expose(wixComps);
            default:
                return [];
        }
    }

    getInstance(scopedRMI, viewerType, compId, controllerId, role) {
        const compType = wixSelectorUtils.getSdkConstructorName(types, scopedRMI, compId);
        const Clazz = ui[compType] || HiddenCollapsedElement;
        if (!viewerType) {
            return [];
        }

        const instance = new Clazz(scopedRMI, compId, this.contextId, this, controllerId, role, {compType, viewerType});
        return !scopedRMI.isGhostComponent(compId) ? instance : ghostMixin(instance);
    }

    getCurrentScopedContext() {
        return this.currentRMI.getCurrentScope();
    }

    createNew$w({
                    currentRMI,
                    globalRMI = currentRMI,
                    context = this.contextId,
                    controller = context
    }) {
        const selector = new this.constructor();

        selector.initialize({currentRMI, globalRMI, contextId: context});

        const $w = selector.scoped$W(controller);
        _.assign($w, platformUtils.getEmitter().createScopedEmitter(controller));

        return $w;
    }

    getGlobalSelector(controller, context) {
        return this.createNew$w({controller, context, currentRMI: this.globalRMI});
    }

    getScopedSelector(compId, scopedContext, scopedController) {
        const scopedRMI = this.currentRMI.getScopedRMIByCompId(compId);

        if(scopedRMI === this.currentRMI) {
            const contextId = scopedContext || this.contextId;
            const controllerId = scopedController || contextId;
            const $w = this.scoped$W(controllerId);
            _.assign($w, platformUtils.getEmitter().createScopedEmitter(controllerId));
            return $w;
        }
        return this.createNew$w({controller: scopedController, context: scopedContext, currentRMI: scopedRMI, globalRMI: this.globalRMI});
    }

    getScopedSelectorForDisplayedOnlyComp(repeaterCompId, itemId, scopedContext, scopedController) {
        const scopedRMI = this.currentRMI.getScopedRMIForRepeaterItem(repeaterCompId, itemId);

        return this.createNew$w({controller: scopedController, context: scopedContext, currentRMI: scopedRMI, globalRMI: this.globalRMI});
    }

    createEvent(type, params) {
        const context = this.getCurrentScopedContext();

        if (_.get(context, 'additionalData.itemId')) {
            context.itemId = context.additionalData.itemId;
        }

        const event = new WixBaseEvent({type, context});

        return _.defaults(event, params);
    }

    at($w, controllerId, context) {
        const currentScopedContext = this.getCurrentScopedContext();

        if (_.isEqual(_.omit(context, ['itemId']), currentScopedContext)) {
            return $w;
        }

        if (this.currentRMI.isRepeaterScope(context)) {
            return this.getScopedSelectorForDisplayedOnlyComp(context.compId, context.additionalData.itemId, null, controllerId);
        }

        return this.getGlobalSelector(controllerId);
    }
}

export default monitoring.monitorClass(WixSelector, function(){
    return {currentRMI: this.currentRMI, contextId: this.contextId};
});
